import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Users, Heart, Home, TrendingUp } from 'lucide-react';

const SocialImpact = () => {
  const [counters, setCounters] = useState({
    meals: 0,
    volunteers: 0,
    beneficiaries: 0,
    villages: 0
  });

  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCounters();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateCounters = () => {
    const targets = {
      meals: 50000,
      volunteers: 53,
      beneficiaries: 52,
      villages: 3
    };

    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setCounters({
        meals: Math.floor(targets.meals * progress),
        volunteers: Math.floor(targets.volunteers * progress),
        beneficiaries: Math.floor(targets.beneficiaries * progress),
        villages: Math.floor(targets.villages * progress)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setCounters(targets);
      }
    }, interval);
  };

  const metrics = [
    {
      icon: Heart,
      value: counters.meals.toLocaleString(),
      label: "Meals Served",
      color: "from-red-500 to-pink-500"
    },
    {
      icon: Users,
      value: counters.volunteers,
      label: "Volunteers",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: TrendingUp,
      value: counters.beneficiaries,
      label: "Beneficiaries Supported",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Home,
      value: counters.villages,
      label: "Sustainable Villages Built",
      color: "from-[#D4AF37] to-[#B8941F]"
    }
  ];

  const initiatives = [
    {
      title: "Community Development",
      description: "Supporting local communities in Thailand, India, Indonesia, and Vietnam through sustainable development programs and co-living initiatives."
    },
    {
      title: "Food Security",
      description: "Through the Tau Foundation, we provide meals and food security to vulnerable populations, ensuring no one goes hungry."
    },
    {
      title: "Sustainable Living",
      description: "Kibouor focuses on building sustainable villages and promoting eco-friendly practices that empower communities for long-term resilience."
    },
    {
      title: "Education & Skills",
      description: "We invest in education and skill development programs to help communities build better futures and create economic opportunities."
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-background via-background to-muted/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Social <span className="text-[#D4AF37]">Impact</span>
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Creating lasting positive change through community empowerment, sustainable development, and social responsibility.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section ref={sectionRef} className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Our Impact in Numbers</h2>
            <p className="text-lg text-muted-foreground">Measurable results across our social initiatives</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {metrics.map((metric, index) => (
              <motion.div
                key={metric.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-card border border-border rounded-xl p-8 text-center hover:shadow-xl hover:border-[#D4AF37]/50 transition-all duration-300"
              >
                <div className={`w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br ${metric.color} flex items-center justify-center`}>
                  <metric.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-4xl font-bold text-foreground mb-2">
                  {metric.value}
                </div>
                <div className="text-muted-foreground font-medium">
                  {metric.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Geographic Presence */}
      <section className="py-20 bg-muted/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Where We Make a Difference</h2>
            <p className="text-lg text-muted-foreground">
              Active across 4 countries with {counters.volunteers} dedicated volunteers
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {['Thailand', 'India', 'Indonesia', 'Vietnam'].map((country, index) => (
              <motion.div
                key={country}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-xl p-6 text-center hover:border-[#D4AF37]/50 transition-all duration-300"
              >
                <div className="text-2xl font-bold text-[#D4AF37] mb-2">📍</div>
                <div className="text-lg font-semibold text-foreground">{country}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Initiatives */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Our Initiatives</h2>
            <p className="text-lg text-muted-foreground">
              Comprehensive programs designed for sustainable, long-term impact
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {initiatives.map((initiative, index) => (
              <motion.div
                key={initiative.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-xl p-8 hover:shadow-xl hover:border-[#D4AF37]/50 transition-all duration-300"
              >
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  {initiative.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {initiative.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Beneficiary Breakdown */}
      <section className="py-20 bg-muted/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Who We Support</h2>
            <p className="text-lg text-muted-foreground">
              Supporting vulnerable populations across all age groups
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-card border border-border rounded-xl p-10 text-center hover:shadow-xl hover:border-[#D4AF37]/50 transition-all duration-300"
            >
              <div className="text-5xl font-bold text-[#D4AF37] mb-4">13</div>
              <div className="text-xl font-semibold text-foreground mb-2">Children</div>
              <p className="text-muted-foreground">Providing education, nutrition, and care</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-card border border-border rounded-xl p-10 text-center hover:shadow-xl hover:border-[#D4AF37]/50 transition-all duration-300"
            >
              <div className="text-5xl font-bold text-[#D4AF37] mb-4">39</div>
              <div className="text-xl font-semibold text-foreground mb-2">Elderly</div>
              <p className="text-muted-foreground">Ensuring dignity, health, and support</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Organizations */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Our CSR Organizations</h2>
            <p className="text-lg text-muted-foreground">
              Dedicated foundations driving social change
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-card to-muted/20 border border-border rounded-xl p-10 hover:shadow-xl hover:border-[#D4AF37]/50 transition-all duration-300"
            >
              <h3 className="text-3xl font-bold text-[#D4AF37] mb-4">Kibouor</h3>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Sustainable Living & Outreach Programs focused on building resilient communities and eco-friendly co-living spaces.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-card to-muted/20 border border-border rounded-xl p-10 hover:shadow-xl hover:border-[#D4AF37]/50 transition-all duration-300"
            >
              <h3 className="text-3xl font-bold text-[#D4AF37] mb-4">Tau Foundation</h3>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Food NGO supporting communities by providing meals, ensuring food security, and promoting nutrition across vulnerable populations.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SocialImpact;

